import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import { useCompany } from "@/contexts/CompanyContext";
import { useAccounting } from "@/contexts/AccountingContext";
import {
  ShieldCheck, Link2, RefreshCw, FileText, AlertTriangle, BarChart3,
  CheckCircle2, XCircle, Clock, ArrowUpRight, ArrowDownLeft, IndianRupee,
  Calendar, Lock, Wifi, WifiOff, Loader2, Search, Download, Eye,
  AlertCircle, TrendingUp, TrendingDown, Server, Shield, Zap,
} from "lucide-react";

type ConnectionStatus = "connected" | "disconnected";
type ApiHealth = "healthy" | "degraded" | "down";

// ========================
// COMPONENTS
// ========================

const StatusBadge = ({ status }: { status: string }) => {
  const map: Record<string, { variant: "default" | "secondary" | "destructive" | "outline"; label: string }> = {
    Filed: { variant: "default", label: "Filed" },
    Pending: { variant: "secondary", label: "Pending" },
    Draft: { variant: "outline", label: "Draft" },
    "Not Started": { variant: "secondary", label: "Not Started" },
    "Auto-generated": { variant: "default", label: "Auto-generated" },
    connected: { variant: "default", label: "Connected" },
    disconnected: { variant: "destructive", label: "Disconnected" },
    ready: { variant: "default", label: "Ready" },
    review: { variant: "secondary", label: "Needs Review" },
    valid: { variant: "default", label: "Valid" },
    warning: { variant: "secondary", label: "Warning" },
    error: { variant: "destructive", label: "Error" },
    healthy: { variant: "default", label: "Healthy" },
  };
  const m = map[status] || { variant: "outline" as const, label: status };
  return <Badge variant={m.variant}>{m.label}</Badge>;
};

const SeverityBadge = ({ severity }: { severity: string }) => {
  if (severity === "high") return <Badge variant="destructive">High</Badge>;
  if (severity === "medium") return <Badge variant="secondary">Medium</Badge>;
  return <Badge variant="outline">Low</Badge>;
};

const INR = (n: number) => `₹${n.toLocaleString("en-IN")}`;

// ========================
// MAIN PAGE
// ========================

const GSTConnectPage = () => {
  const { toast } = useToast();
  const { activeCompany } = useCompany();
  const { vouchers } = useAccounting();
  const [tab, setTab] = useState("dashboard");

  const isConnected = !!((activeCompany?.gstRegistered || (activeCompany as any)?.gst_registered) && activeCompany?.gstin && activeCompany.gstin.length === 15);

  const [gstConnection, setGstConnection] = useState({
    gstin: activeCompany?.gstin || "",
    tradeName: activeCompany?.name || "",
    status: (isConnected ? "connected" : "disconnected") as ConnectionStatus,
    lastSynced: isConnected ? new Date().toISOString() : null,
    apiHealth: (isConnected ? "healthy" : "down") as ApiHealth,
    tokenExpiry: null,
  });

  const [otpStep, setOtpStep] = useState<"idle" | "gstin" | "otp" | "connected">(isConnected ? "connected" : "idle");
  const [gstinInput, setGstinInput] = useState("");
  const [otpInput, setOtpInput] = useState("");
  const [syncing, setSyncing] = useState(false);
  const [validatingInvoice, setValidatingInvoice] = useState("");

  // Derive dynamic accounting metrics
  const salesVouchers = vouchers.filter((v) => v.type === "Sales" && v.status === "Approved");
  const purchaseVouchers = vouchers.filter((v) => v.type === "Purchase" && v.status === "Approved");

  const totalSalesAmount = salesVouchers.reduce((s, v) => s + v.totalDebit, 0);
  const totalPurchasesAmount = purchaseVouchers.reduce((s, v) => s + v.totalDebit, 0);

  const totalGstPayable = salesVouchers.reduce((s, v) => s + (v.gstAmount || 0), 0);
  const totalItc = purchaseVouchers.reduce((s, v) => s + (v.gstAmount || 0), 0);

  const netCredit = totalItc > totalGstPayable ? totalItc - totalGstPayable : 0;
  const netPayable = totalGstPayable > totalItc ? totalGstPayable - totalItc : 0;

  const filingDashboard = {
    gstPayable: {
      cgst: Math.round(totalGstPayable / 2),
      sgst: Math.round(totalGstPayable / 2),
      igst: 0,
      total: totalGstPayable,
    },
    itcAvailable: {
      cgst: Math.round(totalItc / 2),
      sgst: Math.round(totalItc / 2),
      igst: 0,
      total: totalItc,
    },
    netPayable,
    netCredit,
    upcomingDates: isConnected ? [
      { returnType: "GSTR-1", period: new Date().toLocaleDateString("en-IN", { month: "short", year: "numeric" }), dueDate: "11th Next Month", daysLeft: 11 },
      { returnType: "GSTR-3B", period: new Date().toLocaleDateString("en-IN", { month: "short", year: "numeric" }), dueDate: "20th Next Month", daysLeft: 20 },
    ] : [],
    returnStatuses: [] as { period: string; gstr1: string; gstr3b: string; gstr2b: string }[],
  };

  const outwardSupplies = salesVouchers.map((v) => ({
    invoiceNo: v.invoiceNumber || v.voucherNumber,
    party: v.partyName || "Customer",
    gstin: "—",
    taxableValue: v.totalDebit - (v.gstAmount || 0),
    cgst: Math.round((v.gstAmount || 0) / 2),
    sgst: Math.round((v.gstAmount || 0) / 2),
    igst: 0,
    total: v.totalDebit,
    date: v.date,
  }));

  const inwardSupplies = purchaseVouchers.map((v) => ({
    invoiceNo: v.invoiceNumber || v.voucherNumber,
    party: v.partyName || "Supplier",
    gstin: "—",
    taxableValue: v.totalDebit - (v.gstAmount || 0),
    cgst: Math.round((v.gstAmount || 0) / 2),
    sgst: Math.round((v.gstAmount || 0) / 2),
    igst: 0,
    total: v.totalDebit,
    date: v.date,
  }));

  const errors: { id: number; type: string; severity: string; description: string; invoice: string; date: string }[] = [];
  const validationResults: { invoice: string; gstin: boolean; hsn: boolean; rate: boolean; format: boolean; status: string }[] = [];
  const filedReturns: { period: string; type: string; status: string; filedDate: string | null; arn: string | null; taxPayable: number | null }[] = [];
  const apiLogs: { id: number; endpoint: string; method: string; status: number; duration: number; timestamp: string }[] = [];

  const returnsPrep = {
    gstr1: {
      invoices: salesVouchers.length,
      totalTaxable: salesVouchers.reduce((s, v) => s + v.totalDebit - (v.gstAmount || 0), 0),
      totalTax: totalGstPayable,
      status: salesVouchers.length > 0 ? "ready" : "Not Started",
      lastPrepared: salesVouchers.length > 0 ? "Today" : "—",
    },
    gstr3b: {
      salesTax: totalGstPayable,
      purchaseTax: totalItc,
      netPayable,
      status: salesVouchers.length > 0 ? "ready" : "Not Started",
      lastPrepared: salesVouchers.length > 0 ? "Today" : "—",
    },
    gstr2b: {
      invoicesMatched: purchaseVouchers.length,
      invoicesUnmatched: 0,
      totalITC: totalItc,
      status: purchaseVouchers.length > 0 ? "ready" : "Not Started",
      lastPrepared: purchaseVouchers.length > 0 ? "Today" : "—",
    },
  };

  const itcData = {
    cgst: Math.round(totalItc / 2),
    sgst: Math.round(totalItc / 2),
    igst: 0,
    total: totalItc,
    eligible: totalItc,
    ineligible: 0,
    reversed: 0,
  };

  const handleConnect = () => {
    if (otpStep === "idle") {
      setOtpStep("gstin");
    } else if (otpStep === "gstin" && gstinInput.length === 15) {
      setOtpStep("otp");
      toast({ title: "OTP Sent", description: `OTP sent to mobile linked with GSTIN ${gstinInput}` });
    } else if (otpStep === "otp" && otpInput.length === 6) {
      setOtpStep("connected");
      setGstConnection({
        gstin: gstinInput,
        tradeName: activeCompany?.tradeName || activeCompany?.name || "Registered Taxpayer",
        status: "connected",
        lastSynced: new Date().toISOString(),
        apiHealth: "healthy",
        tokenExpiry: null,
      });
      toast({ title: "✅ GST Portal Connected", description: "Your GSTIN has been verified and connected." });
    }
  };

  const handleDisconnect = () => {
    setOtpStep("idle");
    setGstConnection({ ...gstConnection, status: "disconnected" as const });
    toast({ title: "Disconnected", description: "GST portal connection removed." });
  };

  const handleSync = () => {
    setSyncing(true);
    setTimeout(() => {
      setSyncing(false);
      toast({ title: "✅ Sync Complete", description: "All GST data has been fetched from the portal." });
    }, 2500);
  };

  const handlePrepareReturn = (type: string) => {
    toast({ title: `${type} Prepared`, description: `${type} return data has been generated from your accounting records.` });
  };

  const handleFileReturn = (type: string) => {
    toast({ title: `${type} Submitted`, description: `${type} has been submitted to the GST portal for filing.` });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <ShieldCheck className="h-6 w-6 text-primary" /> GST Connect
          </h1>
          <p className="text-muted-foreground mt-1">Automate GST compliance with direct portal integration</p>
        </div>
        <div className="flex items-center gap-3">
          {otpStep === "connected" ? (
            <>
              <div className="flex items-center gap-2 text-sm">
                <Wifi className="h-4 w-4 text-green-500" />
                <span className="text-muted-foreground">Connected</span>
                <code className="text-xs bg-muted px-2 py-0.5 rounded text-foreground">{gstConnection.gstin}</code>
              </div>
              <Button variant="outline" size="sm" onClick={handleSync} disabled={syncing}>
                {syncing ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1" />}
                {syncing ? "Syncing…" : "Sync Now"}
              </Button>
            </>
          ) : (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <WifiOff className="h-4 w-4 text-destructive" /> Not Connected
            </div>
          )}
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="flex flex-wrap h-auto gap-1">
          <TabsTrigger value="dashboard">Filing Dashboard</TabsTrigger>
          <TabsTrigger value="auth">Authentication</TabsTrigger>
          <TabsTrigger value="sync">Data Sync</TabsTrigger>
          <TabsTrigger value="returns">Return Prep</TabsTrigger>
          <TabsTrigger value="errors">Error Detection</TabsTrigger>
          <TabsTrigger value="validation">Invoice Validation</TabsTrigger>
          <TabsTrigger value="reports">Reports</TabsTrigger>
          <TabsTrigger value="gateway">API Gateway</TabsTrigger>
        </TabsList>

        {/* =================== TAB: FILING DASHBOARD =================== */}
        <TabsContent value="dashboard" className="space-y-6">
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="p-4">
                <p className="text-xs text-muted-foreground">GST Payable</p>
                <p className="text-2xl font-bold text-foreground">{INR(filingDashboard.gstPayable.total)}</p>
                <div className="flex gap-2 mt-2 text-xs text-muted-foreground">
                  <span>CGST: {INR(filingDashboard.gstPayable.cgst)}</span>
                  <span>SGST: {INR(filingDashboard.gstPayable.sgst)}</span>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <p className="text-xs text-muted-foreground">ITC Available</p>
                <p className="text-2xl font-bold text-green-600">{INR(filingDashboard.itcAvailable.total)}</p>
                <div className="flex gap-2 mt-2 text-xs text-muted-foreground">
                  <span>CGST: {INR(filingDashboard.itcAvailable.cgst)}</span>
                  <span>IGST: {INR(filingDashboard.itcAvailable.igst)}</span>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <p className="text-xs text-muted-foreground">Net Position</p>
                <p className="text-2xl font-bold text-green-600">Credit: {INR(filingDashboard.netCredit)}</p>
                <p className="text-xs text-muted-foreground mt-2">ITC exceeds liability — carry forward</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <p className="text-xs text-muted-foreground">Errors Detected</p>
                <p className="text-2xl font-bold text-destructive">{errors.length}</p>
                <p className="text-xs text-muted-foreground mt-2">
                  {errors.filter((e) => e.severity === "high").length} high priority
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Upcoming Dates */}
          <Card>
            <CardHeader><CardTitle className="text-base">Upcoming Filing Deadlines</CardTitle></CardHeader>
            <CardContent>
              {filingDashboard.upcomingDates.length === 0 ? (
                <div className="text-center py-6 text-muted-foreground text-sm">
                  {isConnected ? "No upcoming statutory deadlines this week." : "Connect a valid GSTIN to track statutory filing deadlines."}
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {filingDashboard.upcomingDates.map((d) => (
                    <div key={d.returnType} className="flex items-center justify-between p-3 rounded-lg border border-border bg-muted/30">
                      <div>
                        <p className="font-medium text-foreground text-sm">{d.returnType}</p>
                        <p className="text-xs text-muted-foreground">{d.period} — Due {d.dueDate}</p>
                      </div>
                      <Badge variant={d.daysLeft <= 7 ? "destructive" : d.daysLeft <= 15 ? "secondary" : "outline"}>
                        {d.daysLeft} days
                      </Badge>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Return Status Table */}
          <Card>
            <CardHeader><CardTitle className="text-base">Return Filing Status</CardTitle></CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Period</TableHead>
                    <TableHead>GSTR-1</TableHead>
                    <TableHead>GSTR-3B</TableHead>
                    <TableHead>GSTR-2B</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filingDashboard.returnStatuses.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} className="text-center py-6 text-muted-foreground text-sm">
                        No return filing records found. Connect portal to sync status.
                      </TableCell>
                    </TableRow>
                  ) : (
                    filingDashboard.returnStatuses.map((r) => (
                      <TableRow key={r.period}>
                        <TableCell className="font-medium text-foreground">{r.period}</TableCell>
                        <TableCell><StatusBadge status={r.gstr1} /></TableCell>
                        <TableCell><StatusBadge status={r.gstr3b} /></TableCell>
                        <TableCell><StatusBadge status={r.gstr2b} /></TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* =================== TAB: AUTHENTICATION =================== */}
        <TabsContent value="auth" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Connection Card */}
            <Card>
              <CardHeader>
                <CardTitle>GST Portal Authentication</CardTitle>
                <CardDescription>Connect your GSTIN using OTP-based verification</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {otpStep === "connected" ? (
                  <div className="space-y-4">
                    <div className="p-4 rounded-lg border border-green-200 bg-green-50 dark:border-green-900 dark:bg-green-950/30">
                      <div className="flex items-center gap-3">
                        <CheckCircle2 className="h-5 w-5 text-green-600" />
                        <div>
                          <p className="font-medium text-foreground">Connected to GST Portal</p>
                          <p className="text-sm text-muted-foreground">{gstConnection.tradeName}</p>
                        </div>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="text-muted-foreground">GSTIN</p>
                        <p className="font-mono font-medium text-foreground">{gstConnection.gstin}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">API Health</p>
                        <StatusBadge status={gstConnection.apiHealth} />
                      </div>
                      <div>
                        <p className="text-muted-foreground">Last Synced</p>
                        <p className="text-foreground">{new Date(gstConnection.lastSynced).toLocaleString("en-IN")}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Token Expiry</p>
                        <p className="text-foreground">{new Date(gstConnection.tokenExpiry).toLocaleDateString("en-IN")}</p>
                      </div>
                    </div>
                    <Separator />
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={() => toast({ title: "Token Refreshed", description: "API session token has been refreshed." })}>
                        <RefreshCw className="h-4 w-4 mr-1" /> Refresh Token
                      </Button>
                      <Button variant="destructive" size="sm" onClick={handleDisconnect}>Disconnect</Button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {otpStep === "idle" && (
                      <Button onClick={handleConnect} className="w-full">
                        <Link2 className="h-4 w-4 mr-2" /> Connect GST Portal
                      </Button>
                    )}
                    {otpStep === "gstin" && (
                      <div className="space-y-3">
                        <Label>Enter GSTIN</Label>
                        <Input placeholder="27AABCU9603R1ZM" value={gstinInput} onChange={(e) => setGstinInput(e.target.value.toUpperCase())} maxLength={15} className="font-mono" />
                        <p className="text-xs text-muted-foreground">15-character GST Identification Number</p>
                        <Button onClick={handleConnect} disabled={gstinInput.length !== 15}>Send OTP</Button>
                      </div>
                    )}
                    {otpStep === "otp" && (
                      <div className="space-y-3">
                        <div className="p-3 rounded-lg bg-muted/50 border border-border text-sm text-muted-foreground">
                          OTP sent to mobile number linked with GSTIN <span className="font-mono font-medium text-foreground">{gstinInput}</span>
                        </div>
                        <Label>Enter OTP</Label>
                        <Input placeholder="123456" value={otpInput} onChange={(e) => setOtpInput(e.target.value)} maxLength={6} className="font-mono tracking-widest text-center text-lg" />
                        <Button onClick={handleConnect} disabled={otpInput.length !== 6} className="w-full">Verify & Connect</Button>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Security Info */}
            <Card>
              <CardHeader>
                <CardTitle>Security & Compliance</CardTitle>
                <CardDescription>How we protect your GST credentials</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {[
                  { icon: Lock, title: "End-to-End Encryption", desc: "All API communication is encrypted using TLS 1.3" },
                  { icon: Shield, title: "Token-Based Auth", desc: "We never store your GST portal password. Only session tokens." },
                  { icon: Server, title: "Secure Middleware", desc: "All requests routed through our encrypted API gateway" },
                  { icon: Clock, title: "Auto Token Refresh", desc: "Session tokens are auto-refreshed before expiry" },
                ].map((item, i) => (
                  <div key={i} className="flex gap-3 p-3 rounded-lg border border-border bg-card">
                    <item.icon className="h-5 w-5 text-primary mt-0.5 shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-foreground">{item.title}</p>
                      <p className="text-xs text-muted-foreground">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* =================== TAB: DATA SYNC =================== */}
        <TabsContent value="sync" className="space-y-6">
          {/* Sync controls */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              { title: "Filed Returns", desc: "GSTR-1, 3B, 2B returns", icon: FileText, count: filedReturns.length },
              { title: "Outward Supplies", desc: "Sales invoices on portal", icon: ArrowUpRight, count: outwardSupplies.length },
              { title: "Inward Supplies", desc: "Purchase & ITC data", icon: ArrowDownLeft, count: inwardSupplies.length },
            ].map((item) => (
              <Card key={item.title}>
                <CardContent className="p-4 flex items-center gap-4">
                  <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
                    <item.icon className="h-5 w-5 text-primary" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-foreground">{item.title}</p>
                    <p className="text-xs text-muted-foreground">{item.desc}</p>
                  </div>
                  <Badge variant="outline">{item.count} records</Badge>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Filed Returns */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-base">Filed Returns</CardTitle>
                <Button variant="outline" size="sm" onClick={handleSync} disabled={syncing}>
                  {syncing ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1" />} Fetch Latest
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Period</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Filed Date</TableHead>
                    <TableHead>ARN</TableHead>
                    <TableHead className="text-right">Tax (₹)</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filedReturns.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-6 text-muted-foreground text-sm">
                        No filed returns found on portal.
                      </TableCell>
                    </TableRow>
                  ) : (
                    filedReturns.map((r, i) => (
                      <TableRow key={i}>
                        <TableCell className="text-foreground">{r.period}</TableCell>
                        <TableCell><Badge variant="outline">{r.type}</Badge></TableCell>
                        <TableCell><StatusBadge status={r.status} /></TableCell>
                        <TableCell className="text-muted-foreground">{r.filedDate || "—"}</TableCell>
                        <TableCell className="font-mono text-xs text-muted-foreground">{r.arn || "—"}</TableCell>
                        <TableCell className="text-right text-foreground">{r.taxPayable != null ? INR(r.taxPayable) : "—"}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {/* ITC Summary */}
          <Card>
            <CardHeader><CardTitle className="text-base">Input Tax Credit Summary</CardTitle></CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                  { label: "CGST", value: itcData.cgst },
                  { label: "SGST", value: itcData.sgst },
                  { label: "IGST", value: itcData.igst },
                  { label: "Total ITC", value: itcData.total },
                ].map((item) => (
                  <div key={item.label} className="p-3 rounded-lg border border-border bg-muted/30 text-center">
                    <p className="text-xs text-muted-foreground">{item.label}</p>
                    <p className="text-lg font-bold text-foreground">{INR(item.value)}</p>
                  </div>
                ))}
              </div>
              <Separator className="my-4" />
              <div className="grid grid-cols-3 gap-4 text-sm">
                <div><span className="text-muted-foreground">Eligible ITC:</span> <span className="font-medium text-green-600">{INR(itcData.eligible)}</span></div>
                <div><span className="text-muted-foreground">Ineligible:</span> <span className="font-medium text-destructive">{INR(itcData.ineligible)}</span></div>
                <div><span className="text-muted-foreground">Reversed:</span> <span className="font-medium text-foreground">{INR(itcData.reversed)}</span></div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* =================== TAB: RETURN PREPARATION =================== */}
        <TabsContent value="returns" className="space-y-6">
          {[
            { type: "GSTR-1", desc: "Outward Supplies Return", data: returnsPrep.gstr1, fields: [
              { label: "Invoices", value: returnsPrep.gstr1.invoices.toString() },
              { label: "Total Taxable", value: INR(returnsPrep.gstr1.totalTaxable) },
              { label: "Total Tax", value: INR(returnsPrep.gstr1.totalTax) },
            ]},
            { type: "GSTR-3B", desc: "Summary Return", data: returnsPrep.gstr3b, fields: [
              { label: "Output Tax", value: INR(returnsPrep.gstr3b.salesTax) },
              { label: "Input Tax (ITC)", value: INR(returnsPrep.gstr3b.purchaseTax) },
              { label: "Net Payable", value: INR(returnsPrep.gstr3b.netPayable) },
            ]},
            { type: "GSTR-2B", desc: "Auto-drafted ITC Statement", data: returnsPrep.gstr2b, fields: [
              { label: "Matched Invoices", value: returnsPrep.gstr2b.invoicesMatched.toString() },
              { label: "Unmatched", value: returnsPrep.gstr2b.invoicesUnmatched.toString() },
              { label: "Total ITC", value: INR(returnsPrep.gstr2b.totalITC) },
            ]},
          ].map((ret) => (
            <Card key={ret.type}>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-base">{ret.type}</CardTitle>
                    <CardDescription>{ret.desc}</CardDescription>
                  </div>
                  <StatusBadge status={ret.data.status} />
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-3 gap-4">
                  {ret.fields.map((f) => (
                    <div key={f.label} className="p-3 rounded-lg border border-border bg-muted/30 text-center">
                      <p className="text-xs text-muted-foreground">{f.label}</p>
                      <p className="text-lg font-bold text-foreground">{f.value}</p>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground">Last prepared: {ret.data.lastPrepared}</p>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => handlePrepareReturn(ret.type)}>
                    <RefreshCw className="h-4 w-4 mr-1" /> Re-generate
                  </Button>
                  <Button size="sm" onClick={() => handleFileReturn(ret.type)}>
                    <ArrowUpRight className="h-4 w-4 mr-1" /> File {ret.type}
                  </Button>
                  <Button variant="ghost" size="sm">
                    <Download className="h-4 w-4 mr-1" /> Export JSON
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </TabsContent>

        {/* =================== TAB: ERROR DETECTION =================== */}
        <TabsContent value="errors" className="space-y-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { label: "Total Errors", value: errors.length, color: "text-destructive" },
              { label: "Mismatches", value: errors.filter((e) => e.type === "Mismatch").length, color: "text-foreground" },
              { label: "Missing Invoices", value: errors.filter((e) => e.type === "Missing Invoice").length, color: "text-foreground" },
              { label: "High Priority", value: errors.filter((e) => e.severity === "high").length, color: "text-destructive" },
            ].map((s) => (
              <Card key={s.label}>
                <CardContent className="p-4 text-center">
                  <p className="text-xs text-muted-foreground">{s.label}</p>
                  <p className={`text-2xl font-bold ${s.color}`}>{s.value}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-base">GST Discrepancies & Errors</CardTitle>
                <Button variant="outline" size="sm">
                  <Download className="h-4 w-4 mr-1" /> Export Report
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Type</TableHead>
                    <TableHead>Severity</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead>Invoice</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {errors.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-6 text-muted-foreground text-sm">
                        No discrepancies or GST errors detected in recorded transactions.
                      </TableCell>
                    </TableRow>
                  ) : (
                    errors.map((err) => (
                      <TableRow key={err.id}>
                        <TableCell>
                          <Badge variant="outline">{err.type}</Badge>
                        </TableCell>
                        <TableCell><SeverityBadge severity={err.severity} /></TableCell>
                        <TableCell className="text-sm text-foreground max-w-xs">{err.description}</TableCell>
                        <TableCell className="font-mono text-xs text-muted-foreground">{err.invoice}</TableCell>
                        <TableCell className="text-muted-foreground text-sm">{err.date}</TableCell>
                        <TableCell>
                          <Button variant="ghost" size="sm">
                            <Eye className="h-4 w-4 mr-1" /> Review
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* =================== TAB: INVOICE VALIDATION =================== */}
        <TabsContent value="validation" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Invoice Validation</CardTitle>
              <CardDescription>Validate invoices against GST rules before saving</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex gap-3">
                <Input placeholder="Enter Invoice Number (e.g. INV-2026-0142)" value={validatingInvoice} onChange={(e) => setValidatingInvoice(e.target.value)} />
                <Button onClick={() => toast({ title: "Validated", description: `Invoice ${validatingInvoice || "—"} has been validated.` })}>
                  <Search className="h-4 w-4 mr-1" /> Validate
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle className="text-base">Recent Validation Results</CardTitle></CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Invoice</TableHead>
                    <TableHead>GSTIN</TableHead>
                    <TableHead>HSN Code</TableHead>
                    <TableHead>Tax Rate</TableHead>
                    <TableHead>Format</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {validationResults.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-6 text-muted-foreground text-sm">
                        No invoices validated yet. Enter an invoice number above to run validation checks.
                      </TableCell>
                    </TableRow>
                  ) : (
                    validationResults.map((v) => (
                      <TableRow key={v.invoice}>
                        <TableCell className="font-mono text-sm text-foreground">{v.invoice}</TableCell>
                        <TableCell>{v.gstin ? <CheckCircle2 className="h-4 w-4 text-green-500" /> : <XCircle className="h-4 w-4 text-destructive" />}</TableCell>
                        <TableCell>{v.hsn ? <CheckCircle2 className="h-4 w-4 text-green-500" /> : <AlertCircle className="h-4 w-4 text-yellow-500" />}</TableCell>
                        <TableCell>{v.rate ? <CheckCircle2 className="h-4 w-4 text-green-500" /> : <XCircle className="h-4 w-4 text-destructive" />}</TableCell>
                        <TableCell>{v.format ? <CheckCircle2 className="h-4 w-4 text-green-500" /> : <AlertCircle className="h-4 w-4 text-yellow-500" />}</TableCell>
                        <TableCell><StatusBadge status={v.status} /></TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* =================== TAB: REPORTS =================== */}
        <TabsContent value="reports" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[
              { title: "GST Sales Report", desc: "Outward supplies with tax breakdown", icon: TrendingUp, metrics: [
                { label: "Total Sales", value: INR(totalSalesAmount) }, { label: "CGST Collected", value: INR(filingDashboard.gstPayable.cgst) },
                { label: "SGST Collected", value: INR(filingDashboard.gstPayable.sgst) }, { label: "IGST Collected", value: INR(filingDashboard.gstPayable.igst) },
              ]},
              { title: "GST Purchase Report", desc: "Inward supplies and ITC claims", icon: TrendingDown, metrics: [
                { label: "Total Purchases", value: INR(totalPurchasesAmount) }, { label: "CGST Paid", value: INR(itcData.cgst) },
                { label: "SGST Paid", value: INR(itcData.sgst) }, { label: "IGST Paid", value: INR(itcData.igst) },
              ]},
              { title: "Tax Liability Report", desc: "Net tax position for the period", icon: IndianRupee, metrics: [
                { label: "Output Tax", value: INR(filingDashboard.gstPayable.total) }, { label: "Input Tax", value: INR(itcData.total) },
                { label: "Net Credit", value: INR(netCredit) }, { label: "Cash Paid", value: INR(netPayable) },
              ]},
              { title: "ITC Report", desc: "Input Tax Credit eligibility & utilisation", icon: BarChart3, metrics: [
                { label: "Total ITC", value: INR(itcData.total) }, { label: "Eligible", value: INR(itcData.eligible) },
                { label: "Ineligible", value: INR(itcData.ineligible) }, { label: "Reversed", value: INR(itcData.reversed) },
              ]},
            ].map((report) => (
              <Card key={report.title}>
                <CardHeader>
                  <div className="flex items-center gap-3">
                    <div className="h-9 w-9 rounded-lg bg-primary/10 flex items-center justify-center">
                      <report.icon className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <CardTitle className="text-base">{report.title}</CardTitle>
                      <CardDescription>{report.desc}</CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    {report.metrics.map((m) => (
                      <div key={m.label} className="p-2 rounded-lg bg-muted/30 border border-border">
                        <p className="text-xs text-muted-foreground">{m.label}</p>
                        <p className="text-sm font-bold text-foreground">{m.value}</p>
                      </div>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm"><Eye className="h-4 w-4 mr-1" /> View</Button>
                    <Button variant="ghost" size="sm"><Download className="h-4 w-4 mr-1" /> PDF</Button>
                    <Button variant="ghost" size="sm"><Download className="h-4 w-4 mr-1" /> Excel</Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* =================== TAB: API GATEWAY =================== */}
        <TabsContent value="gateway" className="space-y-6">
          {/* Architecture */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Secure API Gateway Architecture</CardTitle>
              <CardDescription>All GST API communication flows through our encrypted middleware</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-center gap-2 flex-wrap p-6 rounded-lg border border-border bg-muted/20">
                {["AccouPro Client", "→", "API Gateway (TLS 1.3)", "→", "Rate Limiter", "→", "Token Manager", "→", "GST Portal API"].map((step, i) => (
                  <div key={i} className={step === "→" ? "text-muted-foreground text-lg" : "px-3 py-2 rounded-lg border border-border bg-card text-sm font-medium text-foreground"}>
                    {step}
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {[
                  { title: "Rate Limiting", desc: "10 req/sec per GSTIN, queuing for bursts", icon: Zap },
                  { title: "Token Management", desc: "Auto-refresh, rotation, encrypted storage", icon: Lock },
                  { title: "Request Logging", desc: "Full audit trail of all API interactions", icon: FileText },
                ].map((f) => (
                  <div key={f.title} className="p-4 rounded-lg border border-border bg-card">
                    <f.icon className="h-5 w-5 text-primary mb-2" />
                    <p className="text-sm font-medium text-foreground">{f.title}</p>
                    <p className="text-xs text-muted-foreground mt-1">{f.desc}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* API Health */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {[
              { label: "Uptime", value: isConnected ? "99.9%" : "—", sub: isConnected ? "Active portal connection" : "Gateway idle" },
              { label: "Avg Latency", value: isConnected ? "120ms" : "—", sub: "GST API response" },
              { label: "Requests Today", value: "0", sub: "Within rate limit" },
              { label: "Errors (24h)", value: "0", sub: "No API errors" },
            ].map((m) => (
              <Card key={m.label}>
                <CardContent className="p-4 text-center">
                  <p className="text-xs text-muted-foreground">{m.label}</p>
                  <p className="text-2xl font-bold text-foreground">{m.value}</p>
                  <p className="text-xs text-muted-foreground mt-1">{m.sub}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* API Logs */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-base">API Request Log</CardTitle>
                <Badge variant="outline">{apiLogs.length} requests</Badge>
              </div>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Timestamp</TableHead>
                    <TableHead>Method</TableHead>
                    <TableHead>Endpoint</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Duration</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {apiLogs.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center py-6 text-muted-foreground text-sm">
                        No API requests logged yet.
                      </TableCell>
                    </TableRow>
                  ) : (
                    apiLogs.map((log) => (
                      <TableRow key={log.id}>
                        <TableCell className="text-muted-foreground text-xs">{new Date(log.timestamp).toLocaleTimeString("en-IN")}</TableCell>
                        <TableCell><Badge variant="outline" className="font-mono text-xs">{log.method}</Badge></TableCell>
                        <TableCell className="font-mono text-xs text-foreground">{log.endpoint}</TableCell>
                        <TableCell>
                          <Badge variant={log.status === 200 ? "default" : "destructive"}>{log.status}</Badge>
                        </TableCell>
                        <TableCell className="text-right text-muted-foreground">{log.duration > 0 ? `${log.duration}ms` : "—"}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default GSTConnectPage;
